import { TrackReferenceOrPlaceholder } from "@livekit/components-react"
import { RemoteVideoTrack, Track } from "livekit-client"
import * as React from "react"
import {
  BackgroundProcessorHandle,
  createBackgroundProcessor,
  supportsBackgroundProcessor,
} from "../../lib/backgroundProcessor"

interface AgentBackgroundVideoProps {
  trackRef: TrackReferenceOrPlaceholder
  imageUrl: string
}

export const AgentBackgroundVideo = React.memo(function AgentBackgroundVideo({
  trackRef,
  imageUrl,
}: AgentBackgroundVideoProps) {
  const outputRef = React.useRef<HTMLVideoElement>(null)
  const [processedReady, setProcessedReady] = React.useState(false)

  const remoteTrack = trackRef.publication?.track as
    RemoteVideoTrack | undefined
  const mediaStreamTrack = remoteTrack?.mediaStreamTrack
  const isCameraVideo =
    trackRef.publication?.kind === Track.Kind.Video &&
    trackRef.source === Track.Source.Camera

  React.useEffect(() => {
    if (!isCameraVideo) return
    if (!supportsBackgroundProcessor()) return
    if (!mediaStreamTrack) return
    const output = outputRef.current
    if (!output) return

    let cancelled = false
    let handle: BackgroundProcessorHandle | null = null

    setProcessedReady(false)

    // Defer one tick so StrictMode's dev mount→cleanup→mount cycle doesn't
    // start two processors that race each other.
    const timer = setTimeout(async () => {
      if (cancelled) return
      try {
        const created = await createBackgroundProcessor(
          mediaStreamTrack as MediaStreamVideoTrack,
          imageUrl,
        )
        if (cancelled) {
          await created.destroy().catch(() => {})
          return
        }
        handle = created
        output.srcObject = new MediaStream([handle.processedTrack])
        await output.play().catch(() => {})
      } catch {
        /* fall through; the static <img> placeholder remains visible */
      }
    }, 0)

    const onPlaying = () => setProcessedReady(true)
    output.addEventListener("playing", onPlaying)

    return () => {
      cancelled = true
      clearTimeout(timer)
      output.removeEventListener("playing", onPlaying)
      output.srcObject = null
      if (handle) {
        handle.destroy().catch(() => {})
        handle = null
      }
    }
  }, [mediaStreamTrack, imageUrl, isCameraVideo])

  if (!isCameraVideo || !supportsBackgroundProcessor()) {
    return null
  }

  return (
    <>
      {processedReady ? (
        <img
          src={imageUrl}
          alt=""
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            pointerEvents: "none",
          }}
        />
      ) : null}
      <video
        ref={outputRef}
        autoPlay
        muted
        playsInline
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          pointerEvents: "none",
          visibility: processedReady ? "visible" : "hidden",
        }}
      />
    </>
  )
})
